/**
 * questionDateRange.ts - Date specs for admin-configured suggested questions
 *
 * Admins can attach a retrieval date filter to a suggested question in the
 * circle's _widget post, e.g.:
 *   <question date="last_7_days">What is the latest news about water scarcity?</question>
 *   <question from="2024-01-01" to="2024-03-31">Q1 2024 policy review</question>
 *
 * Relative tokens follow the grammar  last_<N>_<unit>  where unit is one of
 * hour | day | week | month | year (singular or plural) and N is an optional
 * positive integer that defaults to 1:
 *   last_24_hours, last_7_days, last_45_days, last_3_months, last_18_months,
 *   last_year, last_2_years, last_week ...
 * months/years use calendar math; hours floor to whole days (the filter panel
 * and backend range are date-only). Resolved at tap time relative to "today".
 * Absolute from/to are passed through. Output matches DateRangeFilter so it can
 * be fed straight into the existing filter state and send path.
 */

import { DateRangeFilter } from '@/types/filter.types';

export interface QuestionDateSpec {
    token?: string;
    from?: string;
    to?: string;
}

// last_<N>_<unit> — N optional (defaults to 1), unit singular or plural.
const RELATIVE_TOKEN_RE = /^last_(?:(\d+)_)?(hour|day|week|month|year)s?$/i;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Date-only ISO string (YYYY-MM-DD), matching DateRangeFilter.tsx:92. Built from
// the local components on purpose: toISOString() converts through UTC, which
// reports the wrong calendar day either side of midnight depending on offset.
const toDateString = (date: Date): string => {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
};

// Resolve a last_<N>_<unit> token into a range ending today. Unknown → null.
function resolveRelativeToken(token: string): DateRangeFilter | null {
    const match = token.trim().match(RELATIVE_TOKEN_RE);
    if (!match) return null;

    const n = match[1] ? parseInt(match[1], 10) : 1;
    if (n < 1) return null;

    const from = new Date();
    const customTo = toDateString(from);
    switch (match[2].toLowerCase()) {
        case 'hour': from.setHours(from.getHours() - n); break;
        case 'day': from.setDate(from.getDate() - n); break;
        case 'week': from.setDate(from.getDate() - n * 7); break;
        case 'month': from.setMonth(from.getMonth() - n); break;
        case 'year': from.setFullYear(from.getFullYear() - n); break;
    }
    return { customFrom: toDateString(from), customTo };
}

/**
 * Pull date/from/to attribute values from a <question ...> open-tag attribute
 * string. Supports double or single quotes. Returns undefined when none present.
 */
export function parseQuestionDateAttrs(attrString: string): QuestionDateSpec | undefined {
    if (!attrString) return undefined;

    const read = (name: string): string | undefined => {
        const match = attrString.match(new RegExp(`\\b${name}\\s*=\\s*["']([^"']*)["']`, 'i'));
        const value = match?.[1]?.trim();
        return value ? value : undefined;
    };

    const spec: QuestionDateSpec = {};
    const date = read('date');
    const from = read('from');
    const to = read('to');
    if (date) spec.token = date;
    if (from) spec.from = from;
    if (to) spec.to = to;

    return spec.token || spec.from || spec.to ? spec : undefined;
}

/**
 * Resolve a date spec into a DateRangeFilter.
 * - Relative token → computed range ending today. Unknown token → null.
 * - Absolute from/to → validated passthrough (invalid side omitted). All
 *   invalid → null.
 * A token, when present, takes precedence over from/to.
 */
export function resolveDateRange(spec: QuestionDateSpec): DateRangeFilter | null {
    if (spec.token) {
        return resolveRelativeToken(spec.token);
    }

    const customFrom = spec.from && ISO_DATE_RE.test(spec.from) ? spec.from : null;
    const customTo = spec.to && ISO_DATE_RE.test(spec.to) ? spec.to : null;
    if (!customFrom && !customTo) return null;
    return { customFrom, customTo };
}

/**
 * The range to apply when a question chip is tapped. Unlike resolveDateRange,
 * this never returns null: a question with no date spec (or an unparseable one)
 * must actively CLEAR whatever range a previously tapped chip left behind. The
 * filter lives in the session-wide FilterContext, so "no range" has to mean
 * "cleared", not "unchanged".
 */
export function dateRangeForQuestion(spec: QuestionDateSpec | undefined): DateRangeFilter {
    const resolved = spec ? resolveDateRange(spec) : null;
    return resolved ?? { customFrom: null, customTo: null };
}
